"use client";

import * as React from "react";
import { Check, ChevronLeft, ChevronRight, Loader, Pencil, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { RangeInput } from "@/components/ui/range-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const STEPS = [{
  key: "CONTACT",
  label: "OWNER",
  title: "Ownership Details",
  subtitle: "Provide your contact information for a personalized yield analysis."
}, {
  key: "SCOPE",
  label: "PROPERTY",
  title: "Property Profile",
  subtitle: "Define the asset type and location to benchmark against market data."
}, {
  key: "SPECS",
  label: "METRICS",
  title: "Operational Specs",
  subtitle: "Current management status, amenities, and desired performance."
}, {
  key: "REVIEW",
  label: "REVIEW",
  title: "Verify & Calculate",
  subtitle: "Confirm your property data before generating the estimate."
}];
function ProgressBar({
  current
}: {
  current: number;
}) {
  return <div className="w-full">
      <div className="flex gap-2 w-full" role="progressbar" aria-valuemin={1} aria-valuemax={STEPS.length} aria-valuenow={current + 1}>
        {STEPS.map((s, i) => <div data-index={i} key={s.key} className={["h-[3px] flex-1 transition-all duration-500", i < current ? "bg-primary" : i === current ? "bg-primary animate-pulse" : "bg-muted"].join(" ")} />)}
      </div>
      <div className="mt-3 flex items-center justify-between font-mono text-[10px] tracking-[0.2em] text-white/60">
        {STEPS.map((s, i) => <span data-index={i} key={s.key} className={i === current ? "text-primary" : i < current ? "text-white" : ""}>
            {s.label}
          </span>)}
      </div>
    </div>;
}
function FloatingInput({
  id,
  label,
  type = "text",
  value,
  onChange,
  required
}: {
  id: string;
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}) {
  return <div className="relative pt-5">
      <input id={id} type={type} required={required} value={value} onChange={e => onChange(e.target.value)} placeholder=" " className="peer w-full border-0 border-b border-border bg-transparent px-0 py-2 text-white placeholder-transparent focus:border-primary focus:outline-none focus:ring-0 transition-colors" />
      <label htmlFor={id} className="absolute left-0 top-0 font-mono text-[10px] tracking-[0.2em] uppercase text-white/60 transition-all peer-placeholder-shown:top-7 peer-placeholder-shown:text-sm peer-placeholder-shown:tracking-normal peer-placeholder-shown:font-default peer-placeholder-shown:text-white/60 peer-focus:top-0 peer-focus:text-[10px] peer-focus:tracking-[0.2em] peer-focus:font-mono peer-focus:text-primary">
        {label}
      </label>
    </div>;
}
function ProjectBriefForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const [step, setStep] = React.useState(0);
  const [direction, setDirection] = React.useState<"forward" | "back">("forward");
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [company, setCompany] = React.useState("");
  const [role, setRole] = React.useState("");
  const [projectName, setProjectName] = React.useState("");
  const [projectType, setProjectType] = React.useState("RESIDENTIAL");
  const [timeline, setTimeline] = React.useState("");
  const [budget, setBudget] = React.useState(15000);
  const [substrate, setSubstrate] = React.useState("");
  const [layers, setLayers] = React.useState("");
  const [finish, setFinish] = React.useState("");
  const [files, setFiles] = React.useState<{
    name: string;
    size: number;
  }[]>([]);
  const [notes, setNotes] = React.useState("");
  const [dragOver, setDragOver] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const validateStep = (s: number): boolean => {
    const e: Record<string, string> = {};
    if (s === 0) {
      if (!name.trim()) e.name = "NAME IS REQUIRED";
      if (!email.trim()) e.email = "EMAIL IS REQUIRED";else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = "INVALID EMAIL FORMAT";
    }
    if (s === 1) {
      if (!projectName.trim()) e.projectName = "PROPERTY NAME IS REQUIRED";
      if (!timeline) e.timeline = "SELECT A LOCATION";
    }
    if (s === 2) {
      if (!substrate) e.substrate = "SELECT MANAGEMENT STATUS";
      if (!layers) e.layers = "SELECT BEDROOM COUNT";
      if (!finish) e.finish = "SELECT AMENITY LEVEL";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  const goNext = () => {
    if (!validateStep(step)) return;
    setDirection("forward");
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  };
  const goBack = () => {
    setDirection("back");
    setStep(s => Math.max(s - 1, 0));
  };
  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files).map(f => ({
      name: f.name,
      size: f.size
    }));
    setFiles(prev => [...prev, ...dropped]);
  };
  const onFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const selected = Array.from(e.target.files).map(f => ({
      name: f.name,
      size: f.size
    }));
    setFiles(prev => [...prev, ...selected]);
  };
  const removeFile = (idx: number) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };
  if (isSubmitted && !submitError) {
    return <div className="border border-border bg-card p-10 text-center rounded-md">
        <div className="mx-auto mb-6 flex h-12 w-12 items-center justify-center rounded-md border border-primary text-primary">
          <Check className="h-6 w-6" />
        </div>
        <p className="font-mono text-[10px] tracking-[0.3em] text-primary mb-3 uppercase">§ Analysis Initiated</p>
        <h3 className="font-serif text-3xl font-medium text-foreground mb-3">Report Pending</h3>
        <p className="text-muted-foreground mb-6 max-w-md mx-auto font-default">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="font-mono text-xs tracking-[0.2em] uppercase rounded-md">
          New Calculation
        </Button>
      </div>;
  }
  return <div ref={ref} className={["border border-border bg-card p-6 sm:p-10 lg:p-12 transition-all duration-700 rounded-md", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"].join(" ")}>
      {}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-8">
        <p className="font-mono text-[10px] tracking-[0.3em] text-white/60 uppercase">
          § Yield Calculator / Step {step + 1} of {STEPS.length}
        </p>
        <p className="font-mono text-[10px] tracking-[0.2em] text-white/60 uppercase">
          Est. ~3 min to complete
        </p>
      </div>

      {}
      <ProgressBar current={step} />

      {}
      <div className="mt-10 mb-8">
        <h3 className="font-default text-3xl sm:text-[40px] leading-tight font-semibold text-white">
          {STEPS[step].title}
        </h3>
        <p className="mt-3 text-white/60 text-sm sm:text-base max-w-xl font-default">
          {STEPS[step].subtitle}
        </p>
      </div>

      {}
      <Form onSubmit={handleSubmit}>
        <div className="overflow-hidden">
          <div key={step} className={["transition-all duration-[400ms] ease-out", direction === "forward" ? "animate-in slide-in-from-right-8" : "animate-in slide-in-from-left-8"].join(" ")}>
            {}
            {step === 0 && <div className="space-y-6">
                <FormField control={control} name="name" render={({
              field
            }) => <FormItem>
                      <FormControl>
                        <div>
                          <FloatingInput id="name" label="Full Name" value={field.value || name} onChange={v => {
                    setName(v);
                    field.onChange(v);
                  }} />
                          {errors.name && <p className="mt-2 font-mono text-[10px] tracking-[0.2em] text-primary">
                              {errors.name}
                            </p>}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />

                <FormField control={control} name="email" render={({
              field
            }) => <FormItem>
                      <FormControl>
                        <div>
                          <FloatingInput id="email" label="Email Address" type="email" value={field.value || email} onChange={v => {
                    setEmail(v);
                    field.onChange(v);
                  }} />
                          {errors.email && <p className="mt-2 font-mono text-[10px] tracking-[0.2em] text-primary">
                              {errors.email}
                            </p>}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />

                <FormField control={control} name="phone" render={({
              field
            }) => <FormItem>
                      <FormControl>
                        <FloatingInput id="phone" label="Phone Number" value={field.value || company} onChange={v => {
                  setCompany(v);
                  field.onChange(v);
                }} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />

                <FloatingInput id="role" label="Preferred Contact Method" value={role} onChange={setRole} />
              </div>}

            {}
            {step === 1 && <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField control={control} name="project_title" render={({
              field
            }) => <FormItem className="md:col-span-2">
                      <FormControl>
                        <div>
                          <FloatingInput id="projectName" label="Property Name / Address" value={field.value || projectName} onChange={v => {
                    setProjectName(v);
                    field.onChange(v);
                  }} />
                          {errors.projectName && <p className="mt-2 font-mono text-[10px] tracking-[0.2em] text-primary">
                              {errors.projectName}
                            </p>}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />

                <div className="md:col-span-2">
                  <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-3">
                    Asset Type
                  </p>
                  <RadioGroup value={projectType} onValueChange={setProjectType} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {["RESIDENTIAL", "FRACTIONAL", "COMMERCIAL"].map((opt, i) => <label data-index={i} key={opt} htmlFor={`pt-${opt}`} className={["cursor-pointer border p-4 transition-all flex items-center justify-between gap-3 rounded-md", projectType === opt ? "border-primary bg-accent/30" : "border-border hover:border-muted-foreground/50"].join(" ")}>
                        <span className="font-mono text-xs tracking-[0.2em] text-foreground">{opt}</span>
                        <RadioGroupItem value={opt} id={`pt-${opt}`} />
                      </label>)}
                  </RadioGroup>
                </div>

                <div>
                  <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-3">
                    Location
                  </p>
                  <Select value={timeline} onValueChange={setTimeline}>
                    <SelectTrigger className="w-full border-0 border-b border-border h-11 bg-transparent rounded-md">
                      <SelectValue placeholder="Select region" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cabo-san-lucas">Cabo San Lucas</SelectItem>
                      <SelectItem value="san-jose-del-cabo">San José del Cabo</SelectItem>
                      <SelectItem value="corridor">The Corridor</SelectItem>
                      <SelectItem value="other">Other Baja Regions</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.timeline && <p className="mt-2 font-mono text-[10px] tracking-[0.2em] text-primary">
                      {errors.timeline}
                    </p>}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/60">
                      Estimated Value
                    </p>
                    <p className="font-mono text-xs text-primary">
                      ${budget.toLocaleString()}
                    </p>
                  </div>
                  <RangeInput min={100000} max={5000000} step={50000} value={budget} onValueChange={setBudget} className="w-full" />
                  <div className="mt-2 flex justify-between font-mono text-[10px] tracking-[0.2em] text-white/60">
                    <span>$100K</span>
                    <span>$5M+</span>
                  </div>
                </div>
              </div>}

            {}
            {step === 2 && <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-3">
                      Management
                    </p>
                    <Select value={substrate} onValueChange={setSubstrate}>
                      <SelectTrigger className="w-full border-0 border-b border-border h-11 bg-transparent rounded-md">
                        <SelectValue placeholder="Current Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="self-managed">Self-Managed</SelectItem>
                        <SelectItem value="third-party">Third-Party Firm</SelectItem>
                        <SelectItem value="unmanaged">Unmanaged / Vacant</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.substrate && <p className="mt-2 font-mono text-[10px] tracking-[0.2em] text-primary">
                        {errors.substrate}
                      </p>}
                  </div>

                  <div>
                    <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-3">
                      Bedrooms
                    </p>
                    <Select value={layers} onValueChange={setLayers}>
                      <SelectTrigger className="w-full border-0 border-b border-border h-11 bg-transparent rounded-md">
                        <SelectValue placeholder="Count" />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((n, i) => <SelectItem data-index={i} key={n} value={String(n)}>
                            {n} {n === 1 ? "Bedroom" : "Bedrooms"}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                    {errors.layers && <p className="mt-2 font-mono text-[10px] tracking-[0.2em] text-primary">
                        {errors.layers}
                      </p>}
                  </div>

                  <div>
                    <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-3">
                      Amenity Level
                    </p>
                    <Select value={finish} onValueChange={setFinish}>
                      <SelectTrigger className="w-full border-0 border-b border-border h-11 bg-transparent rounded-md">
                        <SelectValue placeholder="Standard" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="standard">Standard Luxury</SelectItem>
                        <SelectItem value="premium">Premium / Concierge</SelectItem>
                        <SelectItem value="ultra">Ultra-Elite / Private Chef</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.finish && <p className="mt-2 font-mono text-[10px] tracking-[0.2em] text-primary">
                        {errors.finish}
                      </p>}
                  </div>
                </div>

                {}
                <div>
                  <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-3">
                    Property Documentation
                  </p>
                  <div onDragOver={e => {
                e.preventDefault();
                setDragOver(true);
              }} onDragLeave={() => setDragOver(false)} onDrop={onDrop} className={["border border-dashed p-10 flex flex-col items-center justify-center text-center transition-all rounded-md", dragOver ? "border-primary bg-accent/20" : "border-muted-foreground/40"].join(" ")}>
                    <Upload className={["h-6 w-6 mb-4", dragOver ? "text-primary" : "text-muted-foreground"].join(" ")} />
                    <p className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground mb-4 uppercase">
                      Drop Floor Plans / Rental History / Photos
                    </p>
                    <Button type="button" onClick={() => fileInputRef.current?.click()} className="font-mono text-xs tracking-[0.2em] uppercase rounded-md">
                      Upload Files
                    </Button>
                    <input ref={fileInputRef} type="file" multiple className="hidden" onChange={onFileSelect} />
                  </div>

                  {files.length > 0 && <div className="mt-4 flex flex-wrap gap-2">
                      {files.map((f, i) => <div data-index={i} key={`${f.name}-${i}`} className="inline-flex items-center gap-2 border border-border bg-secondary px-3 py-1.5 rounded-md">
                          <span className="font-mono text-[10px] tracking-[0.15em] text-white truncate max-w-[200px]">
                            {f.name}
                          </span>
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeFile(i)} className="h-5 w-5 hover:bg-transparent" aria-label="Remove file">
                            <X className="h-3 w-3 text-muted-foreground hover:text-primary" />
                          </Button>
                        </div>)}
                    </div>}
                </div>

                {}
                <div>
                  <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-3">
                    Specific Requirements
                  </p>
                  <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Mention any specific goals, maintenance concerns, or desired occupancy rates..." className="min-h-32 border-0 border-b border-border bg-transparent focus-visible:ring-0 focus-visible:border-primary px-0 rounded-md" />
                </div>
              </div>}

            {}
            {step === 3 && <div className="space-y-8">
                {[{
              title: "OWNER",
              stepIdx: 0,
              rows: [{
                k: "Full Name",
                v: name || "—"
              }, {
                k: "Email",
                v: email || "—"
              }, {
                k: "Phone",
                v: company || "—"
              }, {
                k: "Contact Pref",
                v: role || "—"
              }]
            }, {
              title: "PROPERTY",
              stepIdx: 1,
              rows: [{
                k: "Asset Name",
                v: projectName || "—"
              }, {
                k: "Asset Type",
                v: projectType
              }, {
                k: "Location",
                v: timeline || "—"
              }, {
                k: "Est. Value",
                v: `$${budget.toLocaleString()}`
              }]
            }, {
              title: "METRICS",
              stepIdx: 2,
              rows: [{
                k: "Management",
                v: substrate || "—"
              }, {
                k: "Bedrooms",
                v: layers || "—"
              }, {
                k: "Amenity Level",
                v: finish || "—"
              }, {
                k: "Documents",
                v: files.length > 0 ? `${files.length} uploaded` : "null"
              }, {
                k: "Notes",
                v: notes || "—"
              }]
            }].map((section, i) => <div data-index={i} key={section.title} className="border-t border-border pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <p className="font-mono text-[10px] tracking-[0.3em] text-primary uppercase">
                        § {section.title}
                      </p>
                      <Button type="button" variant="ghost" size="sm" onClick={() => {
                  setDirection("back");
                  setStep(section.stepIdx);
                }} className="h-7 px-2 font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground hover:text-primary">
                        <Pencil className="h-3 w-3 mr-1.5" />
                        Edit
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                      {section.rows.map((row, j) => <div data-index={j} key={row.k} className="flex justify-between gap-4 border-b border-border/50 pb-2">
                          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/60">
                            {row.k}
                          </span>
                          <span className="text-sm text-white text-right truncate font-default">
                            {row.v}
                          </span>
                        </div>)}
                    </div>
                  </div>)}
              </div>}
          </div>
        </div>

        {submitError && <div className="mt-6 border border-destructive/50 bg-destructive/10 p-4 rounded-md">
            <p className="font-mono text-[10px] tracking-[0.2em] text-destructive uppercase">{submitError}</p>
          </div>}

        {}
        <div className="mt-12 flex items-center justify-between border-t border-border pt-6">
          <Button type="button" variant="ghost" onClick={goBack} disabled={step === 0} className="font-mono text-xs tracking-[0.2em] uppercase text-white hover:text-primary disabled:opacity-30 rounded-md">
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>

          {step < STEPS.length - 1 ? <Button type="button" onClick={goNext} className="font-mono text-xs tracking-[0.2em] uppercase rounded-md">
              Continue
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button> : <Button type="submit" disabled={isSubmitting} className="font-mono text-xs tracking-[0.2em] uppercase rounded-md">
              {isSubmitting ? <>
                  <Loader className="animate-spin mr-2 h-4 w-4" />
                  Calculating
                </> : <>
                  Generate Yield Report
                  <ChevronRight className="h-4 w-4 ml-1" />
                </>}
            </Button>}
        </div>
      </Form>
    </div>;
}
export default function EstimateYourRentalYield() {
  return <section data-section-id="3429" id="estimate-your-rental-yield" className="dark bg-background text-foreground py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[880px]">
        <FormProvider formKey="estimate_rental_yield_form_management" formId="estimate_rental_yield_form_management" sectionName="project-brief" defaultValues={{
        name: "",
        email: "",
        phone: "",
        project_title: "",
        project_description: "",
        target_completion_date: "",
        attachment: ""
      }} validationRules={{
        name: {
          required: true,
          minLength: 2
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: true
        },
        project_title: {
          required: true
        },
        project_description: {
          required: true,
          minLength: 10
        }
      }} submitText="Generate Yield Report" successMessage="Your property data has been received. Our analysts will benchmark your asset against current market trends and deliver your yield report within 24 hours." errorMessage="We could not process your request. Please verify the property details and try again.">
          <ProjectBriefForm />
        </FormProvider>
      </div>
    </section>;
}